/**
 * Store for managing products and pricing
 *
 * Provides CRUD operations for products with tiered pricing.
 * Products are scoped to parade years and sync to Stripe.
 */
import type { Timestamp } from 'firebase/firestore'
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp as FirestoreTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore'
import { defineStore } from 'pinia'
import { useCurrentUser } from 'vuefire'
import type {
  CreateProductInput,
  PricingTier,
  Product,
  ProductFirestoreData,
  ProductType,
  UpdateProductInput,
} from '~/shared/types/product.js'
import { getCurrentTier, isProductAvailable } from '~/shared/types/product.js'
import type { LiveChange, LiveSpec } from '~/utils/liveData'
import { acquireLive, liveDocListener, liveQueryListener } from '~/utils/liveData'

export const useProductsStore = defineStore('products', {
  state: () => ({
    // Store products by document path for O(1) lookups
    products: {} as Record<string, Product>,
    loading: false,
    loaded: false,
  }),

  getters: {
    // Convert map to array for components
    productsArray(): Product[] {
      return Object.values(this.products)
    },

    // Get products by year
    productsByYear(): (year: string) => Product[] {
      return (year: string) => this.productsArray.filter(p => p.year === year)
    },

    // Get products by type
    productsByType(): (type: ProductType) => Product[] {
      return (type: ProductType) => this.productsArray.filter(p => p.type === type)
    },

    // Get active products for a year
    activeProductsForYear(): (year: string) => Product[] {
      return (year: string) => this.productsArray.filter(
        p => p.year === year && p.active,
      )
    },

    // Get available products (active + within sales window + has inventory)
    availableProducts(): Product[] {
      return this.productsArray.filter(isProductAvailable)
    },

    // Get unique years from products
    years(): string[] {
      const yearsSet = new Set(this.productsArray.map(p => p.year))
      return Array.from(yearsSet).sort().reverse()
    },

    // Get a product by ID
    getProductById(): (id: string) => Product | undefined {
      return (id: string) => {
        const path = `products/${id}`
        return this.products[path]
      }
    },
  },

  actions: {
    /**
     * Convert Firestore document data to Product
     */
    convertToProduct(docId: string, data: ProductFirestoreData): Product {
      return {
        id: docId,
        stripeProductId: data.stripeProductId,
        name: data.name,
        description: data.description,
        type: data.type,
        year: data.year,
        active: data.active,
        pricingTiers: data.pricingTiers,
        salesStart: data.salesStart?.toDate?.() ?? null,
        salesEnd: data.salesEnd?.toDate?.() ?? null,
        maxQuantity: data.maxQuantity,
        termsMessage: data.termsMessage ?? null,
        requiresProducts: data.requiresProducts ?? [],
        eligibleMemberTypes: data.eligibleMemberTypes ?? null,
        excludedMemberTypes: data.excludedMemberTypes ?? [],
        quantitySold: data.quantitySold,
        copiedFrom: data.copiedFrom,
        created_at: (data.created_at as Timestamp)?.toDate?.() ?? new Date(),
        updated_at: (data.updated_at as Timestamp)?.toDate?.() ?? new Date(),
        createdBy: data.createdBy,
        updatedBy: data.updatedBy,
      }
    },

    /**
     * Generate a product ID from name and year
     */
    generateProductId(name: string, year: string): string {
      const slug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
      return `${slug}-${year}`
    },

    /**
     * Create a new product
     */
    async createProduct(input: CreateProductInput): Promise<Product | null> {
      try {
        const user = useCurrentUser().value
        if (!user?.uid) {
          console.error('Cannot create product: No authenticated user')
          return null
        }

        const db = getFirestore()

        // Generate ID
        const productId = this.generateProductId(input.name, input.year)

        // Check if product with this ID already exists
        const existingDoc = await getDoc(doc(db, 'products', productId))
        if (existingDoc.exists()) {
          console.error(`Product with ID ${productId} already exists`)
          return null
        }

        // Build pricing tiers with generated IDs
        const pricingTiers: PricingTier[] = input.pricingTiers.map((tier, index) => ({
          id: `tier-${index + 1}`,
          stripePriceId: null,
          unitAmount: tier.unitAmount,
          upToQuantity: tier.upToQuantity,
          label: tier.label,
        }))

        // Build product data
        const productData: Omit<ProductFirestoreData, 'created_at' | 'updated_at'> & {
          created_at: ReturnType<typeof serverTimestamp>
          updated_at: ReturnType<typeof serverTimestamp>
        } = {
          id: productId,
          stripeProductId: null,
          name: input.name,
          description: input.description,
          type: input.type,
          year: input.year,
          active: false, // Start inactive until synced to Stripe
          pricingTiers,
          salesStart: input.salesStart ? FirestoreTimestamp.fromDate(input.salesStart) : null,
          salesEnd: input.salesEnd ? FirestoreTimestamp.fromDate(input.salesEnd) : null,
          maxQuantity: input.maxQuantity ?? null,
          termsMessage: input.termsMessage ?? null,
          requiresProducts: input.requiresProducts ?? [],
          eligibleMemberTypes: input.eligibleMemberTypes ?? null,
          excludedMemberTypes: input.excludedMemberTypes ?? [],
          quantitySold: 0,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
          createdBy: user.uid,
        }

        // Use set with specific ID instead of addDoc
        const docRef = doc(db, 'products', productId)
        const { setDoc } = await import('firebase/firestore')
        await setDoc(docRef, productData)

        const product: Product = {
          ...productData,
          salesStart: input.salesStart ?? null,
          salesEnd: input.salesEnd ?? null,
          termsMessage: input.termsMessage ?? null,
          requiresProducts: input.requiresProducts ?? [],
          eligibleMemberTypes: input.eligibleMemberTypes ?? null,
          excludedMemberTypes: input.excludedMemberTypes ?? [],
          created_at: new Date(),
          updated_at: new Date(),
        }

        // Update local state
        this.$patch((state) => {
          state.products[docRef.path] = product
        })

        return product
      }
      catch (error) {
        console.error('Error creating product:', error)
        return null
      }
    },

    /**
     * Update a product
     */
    async updateProduct(productId: string, updates: UpdateProductInput): Promise<boolean> {
      try {
        const user = useCurrentUser().value
        if (!user?.uid) {
          console.error('Cannot update product: No authenticated user')
          return false
        }

        const db = getFirestore()
        const productRef = doc(db, 'products', productId)
        const productPath = `products/${productId}`

        const product = this.products[productPath]
        if (!product) {
          console.error('Product not found:', productId)
          return false
        }

        // Build update data
        const updateData: Record<string, unknown> = {
          updated_at: serverTimestamp(),
          updatedBy: user.uid,
        }

        if (updates.name !== undefined) updateData.name = updates.name
        if (updates.description !== undefined) updateData.description = updates.description
        if (updates.active !== undefined) updateData.active = updates.active
        if (updates.pricingTiers !== undefined) updateData.pricingTiers = updates.pricingTiers
        if (updates.salesStart !== undefined) updateData.salesStart = updates.salesStart
        if (updates.salesEnd !== undefined) updateData.salesEnd = updates.salesEnd
        if (updates.maxQuantity !== undefined) updateData.maxQuantity = updates.maxQuantity
        if (updates.termsMessage !== undefined) updateData.termsMessage = updates.termsMessage
        if (updates.requiresProducts !== undefined) updateData.requiresProducts = updates.requiresProducts
        if (updates.eligibleMemberTypes !== undefined) updateData.eligibleMemberTypes = updates.eligibleMemberTypes
        if (updates.excludedMemberTypes !== undefined) updateData.excludedMemberTypes = updates.excludedMemberTypes

        await updateDoc(productRef, updateData)

        // Update local state
        this.$patch((state) => {
          state.products[productPath] = {
            ...product,
            ...updates,
            updated_at: new Date(),
            updatedBy: user.uid,
          }
        })

        return true
      }
      catch (error) {
        console.error('Error updating product:', error)
        return false
      }
    },

    /**
     * Delete a product
     */
    async deleteProduct(productId: string): Promise<boolean> {
      try {
        const db = getFirestore()
        const productRef = doc(db, 'products', productId)
        const productPath = `products/${productId}`

        const product = this.products[productPath]
        if (!product) {
          console.error('Product not found:', productId)
          return false
        }

        // Don't allow deleting products that have sales
        if (product.quantitySold > 0) {
          console.error('Cannot delete product with sales:', productId)
          return false
        }

        await deleteDoc(productRef)

        this.$patch((state) => {
          delete state.products[productPath]
        })

        return true
      }
      catch (error) {
        console.error('Error deleting product:', error)
        return false
      }
    },

    /**
     * One product, live while the caller holds it (utils/liveData.ts)
     */
    async fetchProduct(productId: string): Promise<Product | null> {
      try {
        await acquireLive(this.productSpec(productId))
      }
      catch (error) {
        console.error(`Error fetching product ${productId}:`, error)
      }
      return this.products[`products/${productId}`] ?? null
    },

    /**
     * Every product, live while the caller holds it (admins hold them from
     * sign-in)
     */
    async fetchProducts(): Promise<Product[]> {
      this.loading = !this.loaded
      try {
        await acquireLive(this.allProductsSpec())
      }
      catch (error) {
        console.error('Error fetching products:', error)
      }
      finally {
        this.loading = false
      }
      return this.productsArray
    },

    /**
     * One year's products, live while the caller holds them
     */
    async fetchProductsForYear(year: string): Promise<Product[]> {
      try {
        await acquireLive(this.yearProductsSpec(year))
      }
      catch (error) {
        console.error(`Error fetching products for year ${year}:`, error)
      }
      return this.productsByYear(year)
    },

    allProductsSpec(): LiveSpec<ProductFirestoreData> {
      return {
        key: 'products',
        covers: 'products',
        listen: (emit, fail) => onSnapshot(
          query(collection(getFirestore(), 'products'), orderBy('year', 'desc'), orderBy('type'), orderBy('name')),
          ...liveQueryListener('products', emit, fail),
        ),
        apply: changes => this.applyProducts(changes),
        evict: paths => this.evictProducts(paths),
        ready: () => {
          this.loaded = true
        },
        stopped: () => {
          this.loaded = false
        },
      }
    },

    yearProductsSpec(year: string): LiveSpec<ProductFirestoreData> {
      return {
        key: `products?year=${year}`,
        within: 'products',
        listen: (emit, fail) => onSnapshot(
          query(collection(getFirestore(), 'products'), where('year', '==', year), orderBy('type'), orderBy('name')),
          ...liveQueryListener('products', emit, fail),
        ),
        apply: changes => this.applyProducts(changes),
        evict: paths => this.evictProducts(paths),
      }
    },

    productSpec(productId: string): LiveSpec<ProductFirestoreData> {
      const path = `products/${productId}`
      return {
        key: path,
        within: 'products',
        listen: (emit, fail) => onSnapshot(doc(getFirestore(), 'products', productId), ...liveDocListener(path, emit, fail)),
        apply: changes => this.applyProducts(changes),
        evict: paths => this.evictProducts(paths),
      }
    },

    applyProducts(changes: LiveChange<ProductFirestoreData>[]) {
      this.$patch((state) => {
        for (const { path, data } of changes) {
          state.products[path] = this.convertToProduct(path.slice('products/'.length), data!)
        }
      })
    },

    evictProducts(paths: string[]) {
      this.$patch((state) => {
        for (const path of paths) delete state.products[path]
      })
    },

    /**
     * Copy products from one year to another
     */
    async copyProductsToYear(sourceYear: string, targetYear: string, productIds?: string[]): Promise<{
      copied: Product[]
      failed: string[]
    }> {
      const copied: Product[] = []
      const failed: string[] = []

      try {
        const user = useCurrentUser().value
        if (!user?.uid) {
          console.error('Cannot copy products: No authenticated user')
          return { copied, failed }
        }

        // Get source products
        const sourceProducts = productIds
          ? this.productsArray.filter(p => p.year === sourceYear && productIds.includes(p.id))
          : this.productsByYear(sourceYear)

        if (sourceProducts.length === 0) {
          // Try fetching if not loaded
          await this.fetchProductsForYear(sourceYear)
          const refetchedProducts = productIds
            ? this.productsArray.filter(p => p.year === sourceYear && productIds.includes(p.id))
            : this.productsByYear(sourceYear)

          if (refetchedProducts.length === 0) {
            console.warn('No source products found for year:', sourceYear)
            return { copied, failed }
          }
        }

        const productsToCreate = productIds
          ? this.productsArray.filter(p => p.year === sourceYear && productIds.includes(p.id))
          : this.productsByYear(sourceYear)

        for (const source of productsToCreate) {
          // Create new product based on source
          const newProduct = await this.createProduct({
            name: source.name,
            description: source.description,
            type: source.type,
            year: targetYear,
            pricingTiers: source.pricingTiers.map(tier => ({
              id: tier.id,
              unitAmount: tier.unitAmount,
              upToQuantity: tier.upToQuantity,
              label: tier.label,
            })),
            salesStart: null, // Reset dates
            salesEnd: null,
            maxQuantity: source.maxQuantity,
            termsMessage: source.termsMessage,
            requiresProducts: source.requiresProducts,
            eligibleMemberTypes: source.eligibleMemberTypes,
            excludedMemberTypes: source.excludedMemberTypes,
          })

          if (newProduct) {
            // Mark as copied from source
            await this.updateProduct(newProduct.id, {})
            const db = getFirestore()
            const productRef = doc(db, 'products', newProduct.id)
            await updateDoc(productRef, { copiedFrom: source.id })

            copied.push(newProduct)
          }
          else {
            failed.push(source.id)
          }
        }

        return { copied, failed }
      }
      catch (error) {
        console.error('Error copying products:', error)
        return { copied, failed }
      }
    },

    /**
     * Increment quantity sold (called after successful purchase)
     */
    async incrementQuantitySold(productId: string, quantity: number = 1): Promise<boolean> {
      try {
        const db = getFirestore()
        const productRef = doc(db, 'products', productId)
        const productPath = `products/${productId}`

        const product = this.products[productPath]
        if (!product) {
          console.error('Product not found:', productId)
          return false
        }

        const { increment } = await import('firebase/firestore')
        await updateDoc(productRef, {
          quantitySold: increment(quantity),
          updated_at: serverTimestamp(),
        })

        this.$patch((state) => {
          if (state.products[productPath]) {
            state.products[productPath].quantitySold += quantity
            state.products[productPath].updated_at = new Date()
          }
        })

        return true
      }
      catch (error) {
        console.error('Error incrementing quantity sold:', error)
        return false
      }
    },

    /**
     * Decrement quantity sold (called after refund)
     */
    async decrementQuantitySold(productId: string, quantity: number = 1): Promise<boolean> {
      try {
        const db = getFirestore()
        const productRef = doc(db, 'products', productId)
        const productPath = `products/${productId}`

        const product = this.products[productPath]
        if (!product) {
          console.error('Product not found:', productId)
          return false
        }

        const { increment } = await import('firebase/firestore')
        await updateDoc(productRef, {
          quantitySold: increment(-quantity),
          updated_at: serverTimestamp(),
        })

        this.$patch((state) => {
          if (state.products[productPath]) {
            state.products[productPath].quantitySold = Math.max(0, state.products[productPath].quantitySold - quantity)
            state.products[productPath].updated_at = new Date()
          }
        })

        return true
      }
      catch (error) {
        console.error('Error decrementing quantity sold:', error)
        return false
      }
    },

    /**
     * Get the current price for a product based on quantity sold
     */
    getCurrentPrice(productId: string): number | null {
      const product = this.getProductById(productId)
      if (!product) return null

      const tier = getCurrentTier(product)
      return tier.unitAmount
    },

    /**
     * Get pricing summary for a product
     */
    getPricingSummary(productId: string): {
      currentTier: PricingTier
      currentPrice: number
      quantitySold: number
      quantityRemaining: number | null
      nextTierAt: number | null
      isAvailable: boolean
    } | null {
      const product = this.getProductById(productId)
      if (!product) return null

      const currentTier = getCurrentTier(product)
      let nextTierAt: number | null = null

      // Find next tier threshold
      for (const tier of product.pricingTiers) {
        if (tier.upToQuantity !== null && tier.upToQuantity > product.quantitySold) {
          nextTierAt = tier.upToQuantity
          break
        }
      }

      return {
        currentTier,
        currentPrice: currentTier.unitAmount,
        quantitySold: product.quantitySold,
        quantityRemaining: product.maxQuantity !== null
          ? Math.max(0, product.maxQuantity - product.quantitySold)
          : null,
        nextTierAt,
        isAvailable: isProductAvailable(product),
      }
    },
  },
})
